import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { queryClient } from '../query-client';
import { ToastHelper, ToastMessageType, ToastType } from '../helper/toast';
import { FieldValues } from 'react-hook-form';
import { ApiErrorResponse } from '../types/api';
import { parseApiResponseError } from '../helper/parseApiResponseError';
import { TaxesData } from '../types/location';
import { ApiEndpoint } from '../constants/endpoints';

interface EditTaxRatesMutationProps {
  partnerId: string;
}

export const EditTaxRatesMutation = ({
  partnerId,
}: EditTaxRatesMutationProps) => {
  return useMutation({
    mutationFn: (data: FieldValues[]) =>
      api.fetch('edit_tax_rates', {
        data: data,
      }),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [
          'get_default_tax_product_class',
          'tax_rate,product_class',
          1,
          100,
          partnerId,
        ],
      });
      ToastHelper.showToast(
        'Tax Rates',
        ToastType.SUCCESS,
        ToastMessageType.UPDATE,
      );
    },
    onError: (err: ApiErrorResponse) => {
      const errorMessages = parseApiResponseError(err?.response?.data?.error);
      if (errorMessages?.length > 0) {
        errorMessages.map((errorMessage) => {
          ToastHelper.showToast(
            'Tax Rates',
            ToastType.ERROR,
            ToastMessageType.ERROR,
            errorMessage,
          );
        });
      } else {
        ToastHelper.showToast(
          'Tax Rates',
          ToastType.ERROR,
          ToastMessageType.ERROR,
        );
      }
    },
  });
};

interface DeleteTaxRateByIdMutationProps {
  id: string;
  partnerId: string;
  setOpen: React.Dispatch<
    React.SetStateAction<{
      [key: number]: boolean;
    }>
  >;
}

export const DeleteTaxRateByIdMutation = ({
  id,
  partnerId,
  setOpen,
}: DeleteTaxRateByIdMutationProps) => {
  return useMutation({
    mutationFn: () =>
      api.fetch('delete_tax_rate', {
        id: id,
      }),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [
          'get_default_tax_product_class',
          'tax_rate,product_class',
          1,
          100,
          partnerId,
        ],
      });
      ToastHelper.showToast(
        'Tax Rate',
        ToastType.SUCCESS,
        ToastMessageType.DELETE,
      );
      setOpen({});
    },
    onError: () => {
      ToastHelper.showToast(
        'Tax Rate',
        ToastType.ERROR,
        ToastMessageType.ERROR,
      );
    },
  });
};

interface GetTaxRateDataQueryProps {
  partnerId: string;
}

export const GetTaxRateDataQuery = ({
  partnerId,
}: GetTaxRateDataQueryProps) => {
  return useQuery<TaxesData, ApiEndpoint>({
    queryKey: [
      'get_default_tax_product_class',
      'tax_rate,product_class',
      1,
      100,
      partnerId,
    ],
    queryFn: () =>
      api.fetch(`get_default_tax_product_class`, {
        include: 'tax_rate,product_class',
        page: 1,
        pageSize: 100,
        partner: partnerId,
      }) as unknown as TaxesData,
    retry: 0,
  });
};
